/** Circle shape: center [lng, lat], radius in meters */
export type RequestAreaCircle = { type: 'Circle'; center: [number, number]; radius: number };

/** Gate request area: Polygon (GeoJSON) or Circle */
export type RequestAreaGeoJSON =
  | { type: string; coordinates?: number[][] | number[][][] | number[][][][] }
  | RequestAreaCircle
  | null;

function isCircle(geojson: RequestAreaGeoJSON): geojson is RequestAreaCircle {
  return geojson != null && typeof geojson === 'object' && geojson.type === 'Circle';
}

/**
 * Check if point [lng, lat] is inside a GeoJSON Polygon, MultiPolygon, or Circle.
 * Uses ray-casting for polygons; distance check for Circle.
 */
export function pointInGeoJSON(lng: number, lat: number, geojson: RequestAreaGeoJSON): boolean {
  if (!geojson || typeof geojson !== 'object') return false;
  if (isCircle(geojson)) {
    const [cx, cy] = geojson.center;
    const r = geojson.radius;
    if (typeof r !== 'number' || r <= 0) return false;
    const metersPerDegLat = 111320;
    const latRad = (cy * Math.PI) / 180;
    const metersPerDegLng = 111320 * Math.cos(latRad);
    const dLat = (lat - cy) * metersPerDegLat;
    const dLng = (lng - cx) * metersPerDegLng;
    return Math.sqrt(dLat * dLat + dLng * dLng) <= r;
  }
  const withCoords = geojson as { type?: string; coordinates?: number[][][] | number[][][][] };
  if (!withCoords.coordinates?.length) return false;
  const coords = withCoords.coordinates;
  if (withCoords.type === 'MultiPolygon') {
    const polygons = coords as number[][][][];
    for (const poly of polygons) {
      if (poly?.[0]?.length >= 3 && pointInRing(lng, lat, poly[0])) return true;
    }
    return false;
  }
  const ring = (coords as number[][][])[0];
  return !!(ring && ring.length >= 3 && pointInRing(lng, lat, ring));
}

function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi === yj) continue;
    const intersect = (yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi) / (yj - yi)) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Get bounds [[south, west], [north, east]] from a GeoJSON polygon (first ring) or Circle. */
export function getBoundsFromPolygon(geojson: RequestAreaGeoJSON): [[number, number], [number, number]] | null {
  if (!geojson || typeof geojson !== 'object') return null;
  if (isCircle(geojson)) {
    const [lng, lat] = geojson.center;
    const r = geojson.radius;
    if (typeof r !== 'number' || r <= 0) return null;
    const metersPerDegLat = 111320;
    const latRad = (lat * Math.PI) / 180;
    const metersPerDegLng = 111320 * Math.cos(latRad);
    const dLat = r / metersPerDegLat;
    const dLng = r / metersPerDegLng;
    return [[lat - dLat, lng - dLng], [lat + dLat, lng + dLng]];
  }
  const withCoords = geojson as { type?: string; coordinates?: number[][][] | number[][][][] };
  if (!withCoords.coordinates?.length) return null;
  const c = withCoords.coordinates;
  const ring = withCoords.type === 'MultiPolygon' ? (c as number[][][][])[0]?.[0] : (c as number[][][])[0];
  if (!ring?.length) return null;
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const pt of ring) {
    const [x, y] = Array.isArray(pt) && pt.length >= 2 ? pt : [];
    if (typeof x !== 'number' || typeof y !== 'number') continue;
    minLng = Math.min(minLng, x); minLat = Math.min(minLat, y);
    maxLng = Math.max(maxLng, x); maxLat = Math.max(maxLat, y);
  }
  if (minLng === Infinity) return null;
  return [[minLat, minLng], [maxLat, maxLng]];
}
